/**
 * Client-side RSA Encryption Utility
 * 
 * Uses the browser's built-in Web Crypto API (no dependencies).
 * Fetches the server's ephemeral RSA public key and encrypts
 * sensitive fields (e.g. password) before sending them over HTTPS.
 * 
 * This is a defense-in-depth layer ON TOP of TLS.
 */

let cachedKeyId = null;
let cryptoKey = null;

const API_BASE = (() => {
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL.replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && window.location) {
    return window.location.origin.replace(/\/$/, '');
  }
  return '';
})();

/**
 * Convert a PEM-encoded public key to an ArrayBuffer for Web Crypto API
 */
function pemToArrayBuffer(pem) {
  const b64 = pem
    .replace(/-----BEGIN PUBLIC KEY-----/, '')
    .replace(/-----END PUBLIC KEY-----/, '')
    .replace(/\s/g, '');
  const binary = atob(b64);
  const buf = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    buf[i] = binary.charCodeAt(i);
  }
  return buf.buffer;
}

/**
 * Fetch and cache the server's RSA public key
 */
async function fetchPublicKey() {
  try {
    const res = await fetch(`${API_BASE}/api/auth/public-key`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!res.ok) {
      throw new Error(`Failed to fetch public key: ${res.status}`);
    }

    const data = await res.json();

    // If the key hasn't changed, reuse the cached CryptoKey
    if (cachedKeyId === data.keyId && cryptoKey) {
      return cryptoKey;
    }

    // Import the PEM public key into Web Crypto
    const keyBuffer = pemToArrayBuffer(data.publicKey);
    cryptoKey = await window.crypto.subtle.importKey(
      'spki',
      keyBuffer,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256',
      },
      false, // not extractable
      ['encrypt'],
    );

    cachedKeyId = data.keyId;

    return cryptoKey;
  } catch (err) {
    console.error('Failed to fetch RSA public key:', err);
    throw err;
  }
}

/**
 * Encrypt a plaintext string with the server's RSA public key.
 * Returns a Base64-encoded cipher text.
 */
export async function encryptValue(plaintext) {
  const key = await fetchPublicKey();
  const encoded = new TextEncoder().encode(plaintext);

  const encrypted = await window.crypto.subtle.encrypt(
    { name: 'RSA-OAEP' },
    key,
    encoded,
  );

  // Convert ArrayBuffer → Base64
  const bytes = new Uint8Array(encrypted);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Check if RSA encryption is available (Web Crypto API exists)
 */
export function isEncryptionAvailable() {
  return !!(window.crypto && window.crypto.subtle);
}
